'use client';

import { Box, Button, CircularProgress, FormControl, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import { ApolloQueryResult, useLazyQuery, useMutation } from '@parkchirp/api-client';
import { LeaseSubtenantTypeDisplayEnum, LeaseSubtenantTypeEnum } from 'parkchirp-shared/enums';
import React, { Dispatch, FC, SetStateAction, useCallback, useMemo, useState } from 'react';

import { CREATE_LEASE_SUBTENANT } from '../../../mutations';
import { VERIFY_LEASE_SUBTENANT } from '../../../queries';
import { SetShowDialog, SetShowSnackbar } from '../../../types';
import { Subtenant } from '../../../types/leases';
import { leasingRightErrorMessage } from '../../../utils';

type CreateSubtenantFormProps = {
	leasingRight: { id?: number; subscriptionId: string };
	refetchLeasingRight: () => Promise<ApolloQueryResult<any>>;
	subtenants: Subtenant[];
	setSubtenants: Dispatch<SetStateAction<Subtenant[]>>;
	leaseNewMode: boolean;
	newSubtenantId: number;
	setShowDialog: SetShowDialog;
	setShowSnackbar: SetShowSnackbar;
};

export const CreateSubtenantForm: FC<CreateSubtenantFormProps> = ({
	leasingRight,
	refetchLeasingRight,
	subtenants,
	setSubtenants,
	leaseNewMode,
	newSubtenantId,
	setShowDialog,
	setShowSnackbar,
}) => {
	const forcedType = useMemo(() => subtenants[0]?.type ?? null, [subtenants]);

	const [accountId, setAccountId] = useState('');
	const [type, setType] = useState<LeaseSubtenantTypeEnum>(forcedType);

	const [verifyLeaseSubtenant, { loading: verifyInProgress }] = useLazyQuery(VERIFY_LEASE_SUBTENANT);
	const [createLeaseSubtenant, { loading: saveInProgress }] = useMutation(CREATE_LEASE_SUBTENANT);

	const handleSubmit = useCallback(async () => {
		try {
			if (leaseNewMode) {
				const { data, error } = await verifyLeaseSubtenant({
					variables: {
						leaseSubscriptionId: leasingRight.subscriptionId,
						subtenantAccountId: accountId,
						type,
					},
					context: { apiVersion: 'v2' },
				});
				if (error) throw error;

				const subtenant = { id: newSubtenantId, ...data.verifyLeaseSubtenant };
				setSubtenants((prev) => [...prev, subtenant]);

				setShowDialog(null);

				setShowSnackbar({
					message: 'Subtenant Added',
					severity: 'success',
					autoHideDuration: 3000,
				});
			} else {
				await createLeaseSubtenant({
					variables: { leasingRightId: leasingRight.id, accountId, type },
					context: { apiVersion: 'v2' },
				});

				setShowDialog(null);

				setShowSnackbar({
					message: 'Saved successfully',
					severity: 'success',
					autoHideDuration: 5000,
				});

				await refetchLeasingRight();
			}
		} catch (e) {
			console.log(e);
			setShowSnackbar({
				message: leasingRightErrorMessage(e),
				severity: 'error',
			});
		}
	}, [
		accountId,
		createLeaseSubtenant,
		leaseNewMode,
		leasingRight.id,
		leasingRight.subscriptionId,
		newSubtenantId,
		refetchLeasingRight,
		setShowDialog,
		setShowSnackbar,
		setSubtenants,
		type,
		verifyLeaseSubtenant,
	]);

	return (
		<Box display='flex' flexDirection='column' gap='1em' minWidth='25em'>
			<Box display='flex' gap='1em'>
				<TextField
					label='Subtenant Account ID'
					value={accountId}
					onChange={(e) => setAccountId(e.target.value)}
					inputProps={{ 'data-cy': 'account-id-input' }}
				/>
				<FormControl sx={{ width: '50%' }}>
					<InputLabel>Type</InputLabel>
					<Select
						value={type ?? ''}
						label='Type'
						disabled={!!forcedType}
						onChange={(e) => setType(e.target.value as LeaseSubtenantTypeEnum)}
					>
						<MenuItem value={LeaseSubtenantTypeEnum.CHILD}>{LeaseSubtenantTypeDisplayEnum.CHILD}</MenuItem>
						<MenuItem value={LeaseSubtenantTypeEnum.DETACHED}>
							{LeaseSubtenantTypeDisplayEnum.DETACHED}
						</MenuItem>
					</Select>
				</FormControl>
			</Box>
			<Box display='flex' justifyContent='space-between'>
				<Button variant='outlined' color='error' onClick={() => setShowDialog(null)}>
					Cancel
				</Button>
				{saveInProgress || verifyInProgress ? (
					<CircularProgress />
				) : (
					<Button variant='contained' disabled={!accountId || !type} onClick={handleSubmit}>
						Submit
					</Button>
				)}
			</Box>
		</Box>
	);
};
